<template>
  <div class="container">
    <!-- Page Header with Machine Selection -->
    <div class="page-header-bar">
      <div class="box-title">
        <h4>
          <i class="fal fa-route"></i>
          ຕັ້ງຄ່າເສັ້ນທາງສາຂາໄປຕ່າງແຂວງ
          <span class="countTotal" v-if="totalCount">({{ totalCount }})</span>
        </h4>
      </div>
      <!-- Machine Selection - Compact Bar -->
      <div class="machine-select-bar">
        <div class="machine-select-group">
          <label class="select-label">
            <i class="fal fa-server"></i> ເຄື່ອງ:
          </label>
          <model-list-select
            :list="machineOptions"
            v-model="machineCode"
            placeholder="ເລືອກເຄື່ອງຄັດແຍກ..."
            option-text="display_name"
            option-value="machine_code"
            @input="onMachineChanged"
            class="machine-select"
          />
        </div>
      </div>
    </div>

    <!-- Statistics Cards -->
    <div class="stats-section" v-if="statistics">
      <div class="stats-cards">
        <div class="stat-card">
          <div class="stat-icon total">
            <i class="fal fa-layer-group"></i>
          </div>
          <div class="stat-info">
            <span class="stat-value">{{ statistics.total }}</span>
            <span class="stat-label">ທັງໝົດ</span>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-icon active">
            <i class="fal fa-check-circle"></i>
          </div>
          <div class="stat-info">
            <span class="stat-value">{{ statistics.active }}</span>
            <span class="stat-label">ເປີດໃຊ້ງານ</span>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-icon inactive">
            <i class="fal fa-times-circle"></i>
          </div>
          <div class="stat-info">
            <span class="stat-value">{{ statistics.inactive }}</span>
            <span class="stat-label">ປິດໃຊ້ງານ</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Filters Box -->
    <div class="box">
      <div class="section-header">
        <h5 class="section-title">
          <i class="fal fa-filter"></i>
          ຄົ້ນຫາ ແລະ ກັ່ນຕອງ
        </h5>
        <vs-button @click="openAddDialog">
          <i class="fal fa-plus" style="margin-right: 5px"></i>
          ເພີ່ມສາຂາ
        </vs-button>
      </div>
      <div class="filters-row">
        <div class="filter-group">
          <label class="filter-label">ຄົ້ນຫາ:</label>
          <div class="search-input-wrapper">
            <i class="fal fa-search search-icon"></i>
            <input
              type="text"
              v-model="filters.search"
              @keyup.enter="onSearch"
              placeholder="ຊື່ສາຂາ, ລະຫັດ..."
              class="search-input"
            />
          </div>
        </div>
        <div class="filter-group">
          <label class="filter-label">ສູນຄັດແຍກປາຍທາງ:</label>
          <model-list-select
            :list="distributionCenters"
            v-model="filters.distribution_center_id"
            placeholder="ທັງໝົດ"
            option-text="name"
            option-value="id"
            class="filter-select"
          />
        </div>
        <div class="filter-group">
          <label class="filter-label">ແຂວງຕົ້ນທາງ:</label>
          <model-list-select
            :list="sourceProvinceOptions"
            v-model="filters.source_province_id"
            placeholder="ທັງໝົດ"
            option-text="name"
            option-value="id"
            class="filter-select"
          />
        </div>
        <div class="filter-group">
          <label class="filter-label">ສະຖານະ:</label>
          <select v-model="filters.is_active" class="status-select">
            <option value="">ທັງໝົດ</option>
            <option value="1">ເປີດໃຊ້ງານ</option>
            <option value="0">ປິດໃຊ້ງານ</option>
          </select>
        </div>
        <div class="filter-actions">
          <vs-button flat @click="onSearch" :loading="isLoading">
            <i class="fal fa-search" style="margin-right: 5px"></i>
            ຄົ້ນຫາ
          </vs-button>
          <vs-button flat warn @click="resetFilters">
            <i class="fal fa-undo" style="margin-right: 5px"></i>
            ລ້າງ
          </vs-button>
        </div>
      </div>
    </div>

    <!-- Data Table Box -->
    <div class="box">
      <div class="section-header">
        <h5 class="section-title">
          <i class="fal fa-list"></i>
          ລາຍການ
        </h5>
      </div>

      <!-- Loading -->
      <Loading v-if="isLoading" />

      <!-- Data Table -->
      <div class="table-container" v-else>
        <table class="data-table" v-if="overrides.length > 0">
          <thead>
            <tr>
              <th class="col-no">ລຳດັບ</th>
              <th class="col-branch">ສາຂາ</th>
              <th class="col-location">ທີ່ຕັ້ງສາຂາ</th>
              <th class="col-dc">ສູນຄັດແຍກປາຍທາງ</th>
              <th class="col-source">ຕົ້ນທາງ</th>
              <th class="col-status">ສະຖານະ</th>
              <th class="col-created">ສ້າງໂດຍ</th>
              <th class="col-actions">ຈັດການ</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in overrides" :key="item.id">
              <td class="col-no">
                {{ (currentPage - 1) * perPage + index + 1 }}
              </td>
              <td class="col-branch">
                <div class="branch-info">
                  <span class="branch-name">{{ item.branch?.name }}</span>
                  <span class="branch-code">{{
                    item.branch?.prefix || item.branch?.code
                  }}</span>
                </div>
              </td>
              <td class="col-location">
                <div class="location-info">
                  <span class="province-name">
                    ແຂວງ
                    {{ item.branch?.village?.district?.province?.name || "-" }}
                  </span>
                  <span
                    class="district-name"
                    v-if="item.branch?.village?.district"
                  >
                    {{ item.branch.village.district.name }}
                  </span>
                </div>
              </td>
              <td class="col-dc">
                <span class="dc-badge">
                  <i class="fal fa-warehouse"></i>
                  {{ item.distribution_center?.name }}
                </span>
              </td>
              <td class="col-source">
                <span v-if="item.source_province" class="source-badge province">
                  <i class="fal fa-map-marker-alt"></i>
                  {{ item.source_province.name }}
                </span>
                <span v-else-if="item.source_dc" class="source-badge dc">
                  <i class="fal fa-warehouse"></i>
                  {{ item.source_dc.name }}
                </span>
                <span v-else class="global-badge">
                  <i class="fal fa-globe"></i> ທົ່ວໄປ
                </span>
              </td>
              <td class="col-status">
                <span
                  :class="[
                    'status-badge',
                    item.is_active ? 'active' : 'inactive',
                  ]"
                >
                  {{ item.is_active ? "ເປີດໃຊ້ງານ" : "ປິດໃຊ້ງານ" }}
                </span>
              </td>
              <td class="col-created">
                <span class="created-info" v-if="item.created_by">
                  {{ item.created_by.name }}
                </span>
                <span v-else class="text-muted">-</span>
              </td>
              <td class="col-actions">
                <div class="action-buttons">
                  <vs-button
                    flat
                    size="small"
                    @click="openEditDialog(item)"
                    title="ແກ້ໄຂ"
                  >
                    <i class="fal fa-edit"></i>
                  </vs-button>
                  <vs-button
                    flat
                    danger
                    size="small"
                    @click="confirmDelete(item)"
                    title="ລຶບ"
                  >
                    <i class="fal fa-trash"></i>
                  </vs-button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Empty State -->
        <div class="empty-state" v-else>
          <i class="fal fa-inbox"></i>
          <p>ບໍ່ພົບຂໍ້ມູນ</p>
        </div>

        <!-- Pagination -->
        <div class="pagination-container" v-if="totalPages > 1">
          <vs-pagination
            v-model="currentPage"
            :length="totalPages"
            @input="onPageChange"
          />
        </div>
      </div>
    </div>

    <!-- Add/Edit Dialog -->
    <vs-dialog v-model="showDialog" width="600px" prevent-close>
      <template #header>
        <h4 class="dialog-title">
          <i :class="isEditing ? 'fal fa-edit' : 'fal fa-plus'"></i>
          {{ isEditing ? "ແກ້ໄຂເສັ້ນທາງສາຂາ" : "ເພີ່ມເສັ້ນທາງສາຂາ" }}
        </h4>
      </template>

      <div class="dialog-content">
        <!-- Source Province -->
        <div class="form-row">
          <label class="form-label">ແຂວງຕົ້ນທາງ:</label>
          <div class="form-control">
            <model-list-select
              :list="sourceProvinceOptions"
              v-model="form.source_province_id"
              placeholder="ເລືອກແຂວງຕົ້ນທາງ (ຫຼື ປະໄວ້ເປັນທົ່ວໄປ)"
              option-text="name"
              option-value="id"
              @input="onSourceProvinceChange"
              :isDisabled="isSourceProvinceLocked || isEditing"
            />
            <span class="form-hint" v-if="isSourceProvinceLocked && !isEditing">
              <i class="fal fa-lock"></i> ລ໋ອກໄວ້ຕາມເຄື່ອງຄັດແຍກ
            </span>
            <span class="form-hint" v-else-if="!isEditing"
              >ປະໄວ້ຫວ່າງ ຖ້າຕ້ອງການໃຊ້ກັບທຸກແຂວງ</span
            >
          </div>
        </div>

        <!-- Destination DC -->
        <div class="form-row">
          <label class="form-label required">ສູນຄັດແຍກປາຍທາງ:</label>
          <div class="form-control">
            <model-list-select
              :list="distributionCenters"
              v-model="form.distribution_center_id"
              placeholder="ເລືອກສູນຄັດແຍກປາຍທາງ..."
              option-text="name"
              option-value="id"
            />
            <span class="text-danger" v-if="formErrors.distribution_center_id">
              {{ formErrors.distribution_center_id }}
            </span>
          </div>
        </div>

        <!-- Branch Selection (Add Mode) -->
        <div class="form-row" v-if="!isEditing">
          <label class="form-label required">ສາຂາ:</label>
          <div class="form-control">
            <model-list-select
              :list="availableBranches"
              v-model="form.branch_id"
              placeholder="ເລືອກສາຂາ..."
              option-text="display_name"
              option-value="id"
              :isDisabled="isLoadingBranches"
            />
            <span class="text-danger" v-if="formErrors.branch_id">
              {{ formErrors.branch_id }}
            </span>
            <span class="form-hint" v-if="isLoadingBranches">
              <i class="fal fa-spinner fa-spin"></i> ກຳລັງໂຫຼດສາຂາ...
            </span>
          </div>
        </div>

        <!-- Branch Info (Edit Mode) -->
        <div class="form-row" v-if="isEditing && editingItem">
          <label class="form-label">ສາຂາ:</label>
          <div class="form-control">
            <div class="readonly-field">
              {{ formatBranchDisplay(editingItem.branch) }}
            </div>
          </div>
        </div>

        <!-- Active Status -->
        <div class="form-row">
          <label class="form-label">ສະຖານະ:</label>
          <div class="form-control">
            <vs-switch v-model="form.is_active">
              <template #off>ປິດໃຊ້ງານ</template>
              <template #on>ເປີດໃຊ້ງານ</template>
            </vs-switch>
          </div>
        </div>
      </div>

      <template #footer>
        <div class="dialog-footer">
          <vs-button flat @click="closeDialog">ຍົກເລີກ</vs-button>
          <vs-button @click="submitForm" :loading="isSubmitting">
            <i class="fal fa-save" style="margin-right: 5px"></i>
            {{ isEditing ? "ບັນທຶກ" : "ເພີ່ມ" }}
          </vs-button>
        </div>
      </template>
    </vs-dialog>

    <!-- Delete Confirmation Dialog -->
    <vs-dialog v-model="showDeleteDialog" width="400px">
      <template #header>
        <h4 class="dialog-title danger">
          <i class="fal fa-exclamation-triangle"></i>
          ຢືນຢັນການລຶບ
        </h4>
      </template>

      <div class="dialog-content">
        <p v-if="deletingItem">
          ທ່ານແນ່ໃຈບໍ່ວ່າຕ້ອງການລຶບເສັ້ນທາງສຳລັບສາຂາ
          <strong>{{ deletingItem.branch?.name }}</strong
          >?
        </p>
        <div
          class="warning-box"
          v-if="deleteCheckResult && !deleteCheckResult.can_edit"
        >
          <i class="fal fa-exclamation-circle"></i>
          <div>
            <p><strong>ບໍ່ສາມາດລຶບໄດ້:</strong></p>
            <ul>
              <li v-for="(reason, idx) in deleteCheckResult.reasons" :key="idx">
                {{ reason }}
              </li>
            </ul>
          </div>
        </div>
      </div>

      <template #footer>
        <div class="dialog-footer">
          <vs-button flat @click="showDeleteDialog = false">ຍົກເລີກ</vs-button>
          <vs-button
            danger
            @click="deleteOverride"
            :loading="isDeleting"
            :disabled="deleteCheckResult && !deleteCheckResult.can_edit"
          >
            <i class="fal fa-trash" style="margin-right: 5px"></i>
            ລຶບ
          </vs-button>
        </div>
      </template>
    </vs-dialog>
  </div>
</template>

<script>
import Loading from "@/views/Loading";
import { ModelListSelect } from "vue-search-select";

export default {
  name: "BranchDcRoutingOverride",

  components: {
    Loading,
    ModelListSelect,
  },

  data() {
    return {
      // Machine Selection
      machineCode: null,
      machineOptions: [],

      // Data
      overrides: [],
      statistics: null,
      distributionCenters: [],
      provinces: [],
      availableBranches: [],
      machines: [],
      selectedMachine: null,

      // Loading states
      isLoading: false,
      isLoadingBranches: false,
      isSubmitting: false,
      isDeleting: false,

      // Pagination
      currentPage: 1,
      perPage: 15,
      totalPages: 1,
      totalCount: 0,

      // Filters
      filters: {
        search: "",
        distribution_center_id: null,
        source_province_id: null,
        is_active: "",
      },

      // Dialog
      showDialog: false,
      isEditing: false,
      editingItem: null,
      form: {
        branch_id: null,
        distribution_center_id: null,
        source_province_id: null,
        is_active: true,
      },
      formErrors: {},

      // Delete dialog
      showDeleteDialog: false,
      deletingItem: null,
      deleteCheckResult: null,
    };
  },

  computed: {
    sourceProvinceOptions() {
      return this.provinces;
    },
    isSourceProvinceLocked() {
      // Lock source province when only one machine is available
      return this.machines.length === 1;
    },
  },

  created() {
    this.fetchInitialData();
  },

  methods: {
    async fetchInitialData() {
      // Fetch machines first to get the user's province
      await this.fetchMachines();

      await Promise.all([
        this.fetchDistributionCenters(),
        this.fetchProvinces(),
        this.fetchStatistics(),
      ]);
      this.fetchOverrides();
    },

    async fetchMachines() {
      try {
        const response = await this.$http.get("sorting/admin/machines", {
          params: {
            is_active: true,
            find_by_user: true,
          },
        });
        if (response.data && response.data.success) {
          this.machines = response.data.data || [];
          // Create display names for machine options
          this.machineOptions = this.machines.map((m) => ({
            ...m,
            display_name: `${m.machine_code} - ${m.distribution_center_name}`,
          }));
          // Auto-select first machine's province as default filter
          if (this.machines.length > 0) {
            this.selectedMachine = this.machines[0];
            this.machineCode = this.machines[0].machine_code;
            // Set the source province filter to the machine's province
            if (this.selectedMachine.province_id) {
              this.filters.source_province_id =
                this.selectedMachine.province_id;
            }
          }
        } else {
          this.machines = [];
          this.machineOptions = [];
        }
      } catch (error) {
        console.error("Failed to fetch machines:", error);
        this.machines = [];
        this.machineOptions = [];
      }
    },

    async onMachineChanged() {
      // Find the selected machine and update the source province filter
      this.selectedMachine = this.machines.find(
        (m) => m.machine_code === this.machineCode
      );
      if (this.selectedMachine && this.selectedMachine.province_id) {
        this.filters.source_province_id = this.selectedMachine.province_id;
      }
      this.currentPage = 1;
      await this.fetchOverrides();
      await this.fetchStatistics();
    },

    async fetchDistributionCenters() {
      try {
        const response = await this.$http.get(
          "sorting/admin/distribution-centers",
          { params: { routing_level: "province" } }
        );
        if (response.data && response.data.success) {
          this.distributionCenters = response.data.data || [];
        }
      } catch (error) {
        console.error("Failed to fetch distribution centers:", error);
      }
    },

    async fetchProvinces() {
      try {
        const response = await this.$http.get("v1/listing/provinces");
        if (response.data) {
          // Response is a direct array
          this.provinces = Array.isArray(response.data)
            ? response.data
            : response.data.data || [];
        }
      } catch (error) {
        console.error("Failed to fetch provinces:", error);
      }
    },

    async fetchStatistics() {
      try {
        const response = await this.$http.get(
          "sorting/admin/branch-dc-overrides/statistics"
        );
        if (response.data && response.data.success) {
          this.statistics = response.data.data;
        }
      } catch (error) {
        console.error("Failed to fetch statistics:", error);
      }
    },

    async fetchOverrides() {
      this.isLoading = true;
      try {
        const params = {
          page: this.currentPage,
          per_page: this.perPage,
        };

        if (this.filters.search) {
          params.search = this.filters.search;
        }
        if (this.filters.distribution_center_id) {
          params.distribution_center_id = this.filters.distribution_center_id;
        }
        if (this.filters.source_province_id) {
          params.source_province_id = this.filters.source_province_id;
        }
        if (this.filters.is_active !== "") {
          params.is_active = this.filters.is_active;
        }

        const response = await this.$http.get(
          "sorting/admin/branch-dc-overrides",
          { params }
        );

        if (response.data && response.data.success) {
          this.overrides = response.data.data || [];
          if (response.data.pagination) {
            this.totalCount = response.data.pagination.total || 0;
            this.totalPages = response.data.pagination.last_page || 1;
            this.currentPage = response.data.pagination.current_page || 1;
          }
        }
      } catch (error) {
        console.error("Failed to fetch overrides:", error);
        this.$vs.notification({
          color: "danger",
          title: "ເກີດຂໍ້ຜິດພາດ",
          text: "ບໍ່ສາມາດໂຫຼດຂໍ້ມູນໄດ້",
        });
      } finally {
        this.isLoading = false;
      }
    },

    async fetchAvailableBranches() {
      this.isLoadingBranches = true;
      try {
        const params = {};
        if (this.machineCode) {
          params.machine_code = this.machineCode;
        }
        if (this.form.source_province_id) {
          params.source_province_id = this.form.source_province_id;
        }

        const response = await this.$http.get(
          "sorting/admin/branch-dc-overrides/available-branches",
          { params }
        );

        if (response.data && response.data.success) {
          this.availableBranches = (response.data.data || []).map((b) => {
            // Format: [CODE/PREFIX] NAME - DISTRICT, PROVINCE_PREFIX PROVINCE_NAME
            const branchCode = b.code || b.prefix || "";
            const branchName = b.name || "";
            const districtName = `ເມືອງ ${b.district?.name || ""}`;
            const provincePrefix = b.province?.prefix || "";
            const provinceName = b.province?.name || "";

            let displayParts = [];
            if (branchCode) displayParts.push(`[${branchCode}]`);
            displayParts.push(branchName);

            let locationParts = [];
            if (districtName) locationParts.push(districtName);
            if (provincePrefix && provinceName) {
              locationParts.push(`${provincePrefix} ${provinceName}`);
            } else if (provinceName) {
              locationParts.push(provinceName);
            }

            const display_name =
              locationParts.length > 0
                ? `${displayParts.join(" ")} - ${locationParts.join(", ")}`
                : displayParts.join(" ");

            return { ...b, display_name };
          });
        }
      } catch (error) {
        console.error("Failed to fetch available branches:", error);
      } finally {
        this.isLoadingBranches = false;
      }
    },

    formatBranchDisplay(branch) {
      if (!branch) return "-";

      // Format: [CODE/PREFIX] NAME - DISTRICT, PROVINCE_PREFIX PROVINCE_NAME
      const branchCode = branch.code || branch.prefix || "";
      const branchName = branch.name || "";
      const districtName = branch.village?.district?.name
        ? `ເມືອງ ${branch.village.district.name}`
        : "";
      const provincePrefix = branch.village?.district?.province?.prefix || "";
      const provinceName = branch.village?.district?.province?.name || "";

      let displayParts = [];
      if (branchCode) displayParts.push(`[${branchCode}]`);
      displayParts.push(branchName);

      let locationParts = [];
      if (districtName) locationParts.push(districtName);
      if (provincePrefix && provinceName) {
        locationParts.push(`${provincePrefix} ${provinceName}`);
      } else if (provinceName) {
        locationParts.push(provinceName);
      }

      return locationParts.length > 0
        ? `${displayParts.join(" ")} - ${locationParts.join(", ")}`
        : displayParts.join(" ");
    },

    resetFilters() {
      this.filters = {
        search: "",
        distribution_center_id: null,
        source_province_id: null,
        is_active: "",
      };
      this.currentPage = 1;
      this.fetchOverrides();
    },

    onPageChange(page) {
      this.currentPage = page;
      this.fetchOverrides();
    },

    onSearch() {
      this.currentPage = 1;
      this.fetchOverrides();
    },

    openAddDialog() {
      this.isEditing = false;
      this.editingItem = null;
      this.form = {
        branch_id: null,
        distribution_center_id: null,
        // Use machine's province_id as default source province
        source_province_id: this.selectedMachine?.province_id || null,
        is_active: true,
      };
      this.formErrors = {};
      this.availableBranches = [];
      this.showDialog = true;
      this.fetchAvailableBranches();
    },

    openEditDialog(item) {
      this.isEditing = true;
      this.editingItem = item;
      this.form = {
        branch_id: item.branch_id,
        distribution_center_id: item.distribution_center_id,
        source_province_id: item.source_province_id,
        is_active: item.is_active,
      };
      this.formErrors = {};
      this.showDialog = true;
    },

    closeDialog() {
      this.showDialog = false;
      this.isEditing = false;
      this.editingItem = null;
      this.formErrors = {};
    },

    onSourceProvinceChange() {
      if (!this.isEditing) {
        this.form.branch_id = null;
        this.fetchAvailableBranches();
      }
    },

    validateForm() {
      this.formErrors = {};

      if (!this.form.distribution_center_id) {
        this.formErrors.distribution_center_id = "ກະລຸນາເລືອກສູນຄັດແຍກປາຍທາງ";
      }

      if (!this.isEditing && !this.form.branch_id) {
        this.formErrors.branch_id = "ກະລຸນາເລືອກສາຂາ";
      }

      return Object.keys(this.formErrors).length === 0;
    },

    async submitForm() {
      if (!this.validateForm()) return;

      this.isSubmitting = true;
      try {
        let response;
        if (this.isEditing) {
          response = await this.$http.put(
            `sorting/admin/branch-dc-overrides/${this.editingItem.id}`,
            {
              machine_code: this.machineCode,
              distribution_center_id: this.form.distribution_center_id,
              is_active: this.form.is_active,
            }
          );
        } else {
          response = await this.$http.post(
            "sorting/admin/branch-dc-overrides",
            {
              machine_code: this.machineCode,
              branch_id: this.form.branch_id,
              distribution_center_id: this.form.distribution_center_id,
              source_province_id: this.form.source_province_id,
              is_active: this.form.is_active,
            }
          );
        }

        if (response.data && response.data.success) {
          this.$vs.notification({
            color: "success",
            title: "ສຳເລັດ",
            text: this.isEditing ? "ແກ້ໄຂຂໍ້ມູນສຳເລັດ" : "ເພີ່ມຂໍ້ມູນສຳເລັດ",
          });
          this.closeDialog();
          this.fetchOverrides();
          this.fetchStatistics();
        } else {
          this.$vs.notification({
            color: "danger",
            title: "ເກີດຂໍ້ຜິດພາດ",
            text: response.data?.message || "ບໍ່ສາມາດບັນທຶກຂໍ້ມູນໄດ້",
          });
        }
      } catch (error) {
        console.error("Submit error:", error);
        const message =
          error.response?.data?.message || "ບໍ່ສາມາດບັນທຶກຂໍ້ມູນໄດ້";
        this.$vs.notification({
          color: "danger",
          title: "ເກີດຂໍ້ຜິດພາດ",
          text: message,
        });
      } finally {
        this.isSubmitting = false;
      }
    },

    async confirmDelete(item) {
      this.deletingItem = item;
      this.deleteCheckResult = null;
      this.showDeleteDialog = true;

      // Check if can delete
      try {
        const response = await this.$http.get(
          `sorting/admin/branch-dc-overrides/${item.id}/check-usage`
        );
        if (response.data && response.data.success) {
          this.deleteCheckResult = response.data;
        }
      } catch (error) {
        console.error("Check usage error:", error);
      }
    },

    async deleteOverride() {
      if (!this.deletingItem) return;

      this.isDeleting = true;
      try {
        const response = await this.$http.delete(
          `sorting/admin/branch-dc-overrides/${this.deletingItem.id}`
        );

        if (response.data && response.data.success) {
          this.$vs.notification({
            color: "success",
            title: "ສຳເລັດ",
            text: "ລຶບຂໍ້ມູນສຳເລັດ",
          });
          this.showDeleteDialog = false;
          this.deletingItem = null;
          this.fetchOverrides();
          this.fetchStatistics();
        } else {
          this.$vs.notification({
            color: "danger",
            title: "ເກີດຂໍ້ຜິດພາດ",
            text: response.data?.message || "ບໍ່ສາມາດລຶບຂໍ້ມູນໄດ້",
          });
        }
      } catch (error) {
        console.error("Delete error:", error);
        const message = error.response?.data?.message || "ບໍ່ສາມາດລຶບຂໍ້ມູນໄດ້";
        this.$vs.notification({
          color: "danger",
          title: "ເກີດຂໍ້ຜິດພາດ",
          text: message,
        });
      } finally {
        this.isDeleting = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
// Page Header with Machine Selection
.page-header-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  flex-wrap: wrap;
  gap: 10px;

  .box-title {
    margin-bottom: 0;

    h4 {
      margin-bottom: 0;
      font-size: 18px;
      align-items: baseline;

      i {
        margin-right: 8px;
        color: #195bff;
      }

      .countTotal {
        font-size: 14px;
        color: #666;
        font-weight: normal;
      }
    }
  }
}

// Machine Selection Bar
.machine-select-bar {
  display: flex;
  align-items: center;
  gap: 10px;

  .machine-select-group {
    display: flex;
    align-items: center;
    gap: 8px;
    background: #f8f9fa;
    padding: 6px 12px;
    border-radius: 8px;
    border: 1px solid #e0e0e0;

    .select-label {
      font-size: 12px;
      font-weight: 600;
      color: #666;
      white-space: nowrap;

      i {
        margin-right: 4px;
        color: #195bff;
      }
    }

    .machine-select {
      min-width: 180px;
    }
  }
}

// Compact spacing for better vertical view
.container {
  .box {
    margin-bottom: 10px;
    padding: 12px 15px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .box-title {
    margin-bottom: 10px;

    h4 {
      margin-bottom: 0;
      font-size: 18px;
      align-items: baseline;

      i {
        margin-right: 8px;
        color: #195bff;
      }

      .countTotal {
        font-size: 14px;
        color: #666;
        font-weight: normal;
      }
    }
  }
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  flex-wrap: wrap;
  gap: 10px;

  .section-title {
    margin-bottom: 0;
    flex-shrink: 0;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;

    .vs-button {
      width: 100%;
    }
  }
}

.section-title {
  font-size: 15px;
  font-weight: 600;
  color: #195bff;
  margin-bottom: 0;

  i {
    margin-right: 6px;
  }
}

// Stats Section
.stats-section {
  margin-bottom: 10px;
}

.stats-cards {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
}

.stat-card {
  display: flex;
  align-items: center;
  padding: 10px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
  border: 1px solid #eee;

  .stat-icon {
    width: 38px;
    height: 38px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    margin-right: 10px;

    i {
      font-size: 18px;
      color: white;
    }

    &.total {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    }
    &.active {
      background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
    }
    &.inactive {
      background: linear-gradient(135deg, #eb3349 0%, #f45c43 100%);
    }
  }

  .stat-info {
    .stat-value {
      display: block;
      font-size: 18px;
      font-weight: 700;
      color: #333;
    }

    .stat-label {
      display: block;
      font-size: 11px;
      color: #666;
    }
  }
}

.filters-row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: flex-end;
  padding: 10px;
  background: #f9f9f9;
  border-radius: 8px;

  .filter-group {
    display: flex;
    flex-direction: column;
    gap: 4px;

    .filter-label {
      font-size: 11px;
      font-weight: 600;
      color: #666;
    }

    .filter-select {
      min-width: 160px;
    }
  }

  .search-input-wrapper {
    position: relative;

    .search-icon {
      position: absolute;
      left: 10px;
      top: 50%;
      transform: translateY(-50%);
      color: #999;
      font-size: 12px;
    }

    .search-input {
      width: 180px;
      padding: 7px 10px 10px 30px;
      border: 1px solid #e0e0e0;
      border-radius: 6px;
      font-size: 12px;
      outline: none;
      font-family: "Open Sans", "BoonBaan", "Noto Sans Lao", sans-serif;

      &:focus {
        border-color: #195bff;
      }
    }
  }

  .status-select {
    padding: 8px 10px;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    font-size: 12px;
    outline: none;
    min-width: 110px;

    &:focus {
      border-color: #195bff;
    }
  }

  .filter-actions {
    display: flex;
    align-items: flex-end;
    gap: 6px;
    padding-top: 15px; // Align with filter-group labels

    // Override Vuesax button margins
    ::v-deep .vs-button {
      margin: 0 !important;
      min-height: 36px;
    }
  }

  @media (max-width: 768px) {
    .filter-group {
      width: 100%;

      .search-input-wrapper .search-input,
      .filter-select,
      .status-select {
        width: 100%;
      }
    }

    .filter-actions {
      width: 100%;

      .vs-button {
        flex: 1;
      }
    }
  }
}

.table-container {
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
}

.data-table {
  width: 100%;
  border-collapse: collapse;

  thead {
    background: #f8f9fa;

    th {
      padding: 12px;
      text-align: left;
      font-weight: 600;
      font-size: 13px;
      color: #333;
      border-bottom: 2px solid #e0e0e0;
      white-space: nowrap;
    }
  }

  tbody {
    tr {
      border-bottom: 1px solid #f0f0f0;
      transition: background 0.2s;

      &:hover {
        background: #fafafa;
      }
    }

    td {
      padding: 12px;
      font-size: 13px;
      color: #333;
    }
  }

  .col-no {
    width: 60px;
    text-align: center;
    color: #999;
  }

  .col-branch {
    .branch-info {
      .branch-name {
        display: block;
        font-weight: 600;
        color: #333;
      }
      .branch-code {
        font-size: 11px;
        color: #666;
      }
    }
  }

  .col-location {
    .location-info {
      .province-name {
        display: block;
        font-weight: 500;
        color: #333;
      }
      .district-name {
        display: block;
        font-size: 11px;
        color: #666;
      }
    }
  }

  .col-created {
    .created-info {
      font-size: 12px;
      color: #333;
    }
    .text-muted {
      color: #999;
    }
  }

  .dc-badge {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 4px 10px;
    background: #e3f2fd;
    color: #1565c0;
    border-radius: 15px;
    font-size: 12px;
    font-weight: 500;

    i {
      font-size: 11px;
    }
  }

  .source-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 10px;
    border-radius: 15px;
    font-size: 12px;

    i {
      font-size: 11px;
    }

    &.province {
      background: #fff3e0;
      color: #ef6c00;
    }

    &.dc {
      background: #e8f5e9;
      color: #2e7d32;
    }
  }

  .global-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 10px;
    background: #f5f5f5;
    color: #666;
    border-radius: 15px;
    font-size: 12px;

    i {
      font-size: 11px;
    }
  }

  .status-badge {
    display: inline-block;
    padding: 4px 10px;
    border-radius: 15px;
    font-size: 11px;
    font-weight: 600;

    &.active {
      background: #e8f5e9;
      color: #2e7d32;
    }
    &.inactive {
      background: #ffebee;
      color: #c62828;
    }
  }

  .action-buttons {
    display: flex;
    gap: 5px;
  }
}

.empty-state {
  padding: 60px 20px;
  text-align: center;
  color: #999;

  i {
    font-size: 48px;
    margin-bottom: 15px;
    color: #ddd;
  }

  p {
    font-size: 16px;
    margin: 0;
  }
}

.pagination-container {
  display: flex;
  justify-content: center;
  padding: 20px;
  border-top: 1px solid #f0f0f0;
}

// Dialog styles
.dialog-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0;

  i {
    margin-right: 8px;
    color: #195bff;
  }

  &.danger i {
    color: #c62828;
  }
}

.dialog-content {
  padding: 15px 0;
}

// Horizontal form layout
.form-row {
  display: flex;
  align-items: flex-start;
  margin-bottom: 16px;

  .form-label {
    flex-shrink: 0;
    width: 140px;
    font-size: 13px;
    font-weight: 600;
    color: #333;
    padding-top: 8px;
    text-align: right;
    padding-right: 12px;

    &.required::after {
      content: " *";
      color: #c62828;
    }
  }

  .form-control {
    flex: 1;
    min-width: 0;

    .form-hint {
      display: block;
      font-size: 11px;
      color: #999;
      margin-top: 4px;
    }

    .text-danger {
      display: block;
      font-size: 11px;
      color: #c62828;
      margin-top: 4px;
    }

    .readonly-field {
      padding: 8px 12px;
      background: #f5f5f5;
      border-radius: 6px;
      font-size: 13px;

      .branch-name {
        font-weight: 600;
      }
      .branch-code {
        color: #666;
        margin-left: 5px;
      }
    }

    // Fix vs-switch width and z-index overlay issue
    ::v-deep .vs-switch {
      width: auto !important;
      max-width: fit-content;
      position: relative;
      z-index: 1;
    }
  }

  @media (max-width: 576px) {
    flex-direction: column;

    .form-label {
      width: 100%;
      text-align: left;
      padding-right: 0;
      padding-top: 0;
      margin-bottom: 6px;
    }

    .form-control {
      width: 100%;
    }
  }
}

.form-group {
  margin-bottom: 18px;

  .form-label {
    display: block;
    font-size: 13px;
    font-weight: 600;
    color: #333;
    margin-bottom: 8px;

    &.required::after {
      content: " *";
      color: #c62828;
    }
  }

  .form-hint {
    display: block;
    font-size: 11px;
    color: #999;
    margin-top: 5px;
  }

  .text-danger {
    display: block;
    font-size: 11px;
    color: #c62828;
    margin-top: 5px;
  }

  .readonly-field {
    padding: 10px 12px;
    background: #f5f5f5;
    border-radius: 6px;
    font-size: 14px;

    .branch-name {
      font-weight: 600;
    }
    .branch-code {
      color: #666;
      margin-left: 5px;
    }
  }

  // Fix vs-switch width
  ::v-deep .vs-switch {
    width: auto !important;
    max-width: fit-content;
  }
}

.warning-box {
  display: flex;
  gap: 12px;
  padding: 15px;
  background: #fff3e0;
  border-radius: 8px;
  border-left: 4px solid #f57c00;
  margin-top: 15px;

  > i {
    color: #f57c00;
    font-size: 20px;
    flex-shrink: 0;
  }

  p {
    margin: 0 0 8px 0;
    font-size: 13px;
  }

  ul {
    margin: 0;
    padding-left: 18px;

    li {
      font-size: 12px;
      color: #666;
      margin-bottom: 4px;
    }
  }
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

// Deep styles for model-list-select
::v-deep .ui.search.dropdown {
  min-height: 36px !important;
  width: 100% !important;
  border-radius: 6px !important;
  font-size: 12px !important;

  > .text {
    font-size: 12px !important;
  }

  input.search {
    padding: 6px 10px !important;
    font-size: 12px !important;
  }

  .menu {
    font-size: 12px !important;
    z-index: 100 !important;

    > .item {
      padding: 8px 10px !important;
      font-size: 12px !important;
    }
  }
}
</style>
